import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/modules/auth';
import { BrandLogo } from '@/components/BrandLogo';
import { cn } from '@/lib/utils';

/* ─── Animation Config ──────────────────────────────────────────────────── */

const ease = [0.22, 1, 0.36, 1] as const;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1.0, ease },
};

const fadeUpView = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 1.0, ease, delay },
});

/* ─── Data ──────────────────────────────────────────────────────────────── */

interface Tier {
  name: string;
  price: string;
  period: string;
  subtitle: string;
  badge: string | null;
  features: string[];
  cta: string;
  href: string;
  highlighted: boolean;
  isExternal?: boolean;
  comingSoon?: boolean;
}

const tiers: Tier[] = [
  {
    name: 'Maintenance AI',
    price: 'From $700',
    period: '/month',
    subtitle: 'Your AI maintenance coordinator',
    badge: null,
    features: [
      'Tenant maintenance request intake',
      'AI triage and issue classification',
      'Automated tradie coordination',
      'Quote collection and comparison',
      'Owner approval workflows',
      'Real-time status updates to all parties',
      'Email and in-app notifications',
    ],
    cta: 'Get Started',
    href: '/auth?mode=signup&role=property_manager&plan=maintenance',
    highlighted: false,
  },
  {
    name: 'Full AI Ops',
    price: 'From $1,500',
    period: '/month',
    subtitle: 'Your complete AI operations team',
    badge: 'Coming Soon',
    features: [
      'Everything in Maintenance AI, plus:',
      'Compliance deadline tracking',
      'Automated admin reminders',
      'Arrears follow-up',
      'Lease renewal support',
      'Portfolio risk reporting',
      'Priority support',
    ],
    cta: 'Join Waitlist',
    href: 'mailto:hello@agentiveproperty.ai?subject=Full AI Ops Waitlist',
    highlighted: true,
    isExternal: true,
    comingSoon: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    subtitle: 'For large portfolios and franchise groups',
    badge: null,
    features: [
      'Everything in Full AI Ops, plus:',
      'Dedicated onboarding',
      'Custom integrations',
      'Volume pricing',
      'SLA guarantees',
    ],
    cta: 'Contact Us',
    href: 'mailto:hello@agentiveproperty.ai',
    highlighted: false,
    isExternal: true,
  },
];

const faqs = [
  {
    question: 'How does pricing work?',
    answer:
      'Pricing is based on portfolio size and service level. The listed prices are starting points — we tailor a quote to your specific needs during onboarding.',
  },
  {
    question: 'Is there a free trial?',
    answer:
      'We offer a guided pilot for qualified agencies. Get in touch to discuss your portfolio and we\'ll design a trial that demonstrates real value.',
  },
  {
    question: 'Can I switch plans later?',
    answer:
      'Yes. You can upgrade or adjust your plan at any time as your portfolio grows or your needs change.',
  },
  {
    question: 'What does onboarding look like?',
    answer:
      'We handle everything. Our team configures your AI workforce, imports your portfolio data, and trains your team — typically live within 2 weeks.',
  },
  {
    question: 'Does it work with my property management system?',
    answer:
      'Our AI workforce integrates with major property management solutions including PropertyMe, MRI, and Console. Contact us for specific compatibility.',
  },
];

/* ─── Component ─────────────────────────────────────────────────────────── */

const PricingPage: React.FC = () => {
  const { user, userRole } = useAuth();
  const [navScrolled, setNavScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.title = 'Pricing — Hire Your AI Property Team | Agentive Property';
    const meta = document.querySelector('meta[name="description"]');
    const canonical = document.querySelector('link[rel="canonical"]');
    const origTitle = 'Agentive Property — The AI Workforce for Property Maintenance';
    const origDesc = meta?.getAttribute('content') || '';
    const origCanonical = canonical?.getAttribute('href') || '';

    if (meta) meta.setAttribute('content', 'AI workforce pricing for property management. From $700/month for maintenance coordination to full AI operations. Replace manual admin with 24/7 AI workers.');
    if (canonical) canonical.setAttribute('href', 'https://www.agentiveproperty.ai/pricing');

    return () => {
      document.title = origTitle;
      if (meta) meta.setAttribute('content', origDesc);
      if (canonical) canonical.setAttribute('href', origCanonical);
    };
  }, []);

  const getDashboardLink = () => {
    if (!user) return '/auth';
    switch (userRole) {
      case 'tradie': return '/tradie';
      case 'admin': return '/admin';
      case 'property_manager': return '/pm';
      default: return '/dashboard';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ── 1. Navigation ─────────────────────────────────────────────── */}
      <nav
        className={cn(
          'fixed top-0 left-0 right-0 z-50 bg-white transition-[border-color] duration-300',
          navScrolled ? 'border-b border-border' : 'border-b border-transparent'
        )}
      >
        <div className="landing-container h-16 flex items-center justify-between">
          <Link to="/">
            <BrandLogo size="sm" />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/#how-it-works"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              How It Works
            </Link>
            <Link
              to="/pricing"
              className="text-sm text-foreground font-medium transition-colors"
            >
              Pricing
            </Link>
            <Link
              to="/#platform"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Roadmap
            </Link>
            <Link
              to="/#results"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Results
            </Link>
          </div>

          <div className="flex items-center gap-3">
            {user ? (
              <Button asChild variant="default" size="sm">
                <Link to={getDashboardLink()}>Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm">
                  <Link to="/auth?mode=login">Sign In</Link>
                </Button>
                <Button asChild variant="default" size="sm">
                  <Link to="/pricing">Get Started</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ── 2. Hero ───────────────────────────────────────────────────── */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="landing-container text-center">
          <motion.p {...fadeUp} className="landing-label mb-4">
            Pricing
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-foreground leading-[1.1]"
          >
            Hire Your{' '}
            <span className="text-gradient">AI Property Team</span>
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto"
          >
            Replace manual coordination with AI workers that execute 24/7
          </motion.p>
        </div>
      </section>

      {/* ── 3. Pricing Tiers ──────────────────────────────────────────── */}
      <section className="pb-24 md:pb-32">
        <div className="landing-container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-4">
            {tiers.map((tier, i) => (
              <motion.div
                key={tier.name}
                {...fadeUpView(i * 0.1)}
                className={cn(
                  'rounded-2xl border p-8 flex flex-col relative',
                  tier.highlighted
                    ? 'border-accent ring-2 ring-accent/20'
                    : 'border-border bg-white'
                )}
              >
                {tier.badge && (
                  <span className={cn(
                    "absolute -top-4 left-1/2 -translate-x-1/2 text-white text-xs font-semibold px-4 py-1.5 rounded-full whitespace-nowrap",
                    tier.comingSoon ? "bg-muted-foreground" : "gradient-accent"
                  )}>
                    {tier.badge}
                  </span>
                )}

                <h3 className="text-lg font-semibold text-foreground">{tier.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">{tier.subtitle}</p>

                <div className="mt-6">
                  <span className="text-4xl font-bold text-foreground">{tier.price}</span>
                  {tier.period && (
                    <span className="text-lg text-muted-foreground font-normal">{tier.period}</span>
                  )}
                </div>

                <ul className="mt-8 space-y-3 flex-1">
                  {tier.features.map((feature) => {
                    const isGroupLabel = feature.includes('Everything in');
                    return (
                      <li key={feature} className="flex items-start gap-3">
                        {!isGroupLabel && (
                          <Check className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        )}
                        <span
                          className={cn(
                            'text-sm',
                            isGroupLabel
                              ? 'font-medium text-foreground'
                              : 'text-muted-foreground'
                          )}
                        >
                          {feature}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                <div className="mt-8">
                  {tier.isExternal ? (
                    <Button asChild variant="outline" size="lg" className="w-full">
                      <a href={tier.href}>
                        {tier.cta}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </a>
                    </Button>
                  ) : (
                    <Button
                      asChild
                      variant={tier.highlighted ? 'default' : 'outline'}
                      size="lg"
                      className="w-full"
                    >
                      <Link to={tier.href}>
                        {tier.cta}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.p
            {...fadeUpView(0.3)}
            className="mt-12 text-center text-muted-foreground text-sm max-w-2xl mx-auto"
          >
            A human maintenance coordinator costs $5,000&ndash;$7,000/month. Your AI coordinator starts at $700.
          </motion.p>
        </div>
      </section>

      {/* ── 4. ROI Comparison ─────────────────────────────────────────── */}
      <section className="landing-section bg-background">
        <div className="landing-container">
          <motion.p {...fadeUpView()} className="landing-label text-center mb-4">
            The ROI
          </motion.p>
          <motion.h2 {...fadeUpView(0.1)} className="landing-heading text-center mb-16">
            Stop paying for manual coordination
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* What you're replacing */}
            <motion.div
              {...fadeUpView(0.1)}
              className="rounded-2xl border border-border bg-white p-8"
            >
              <h3 className="text-xl font-semibold text-foreground mb-6">
                What you're replacing
              </h3>
              <ul className="space-y-4">
                {[
                  'Manual coordinator ($5k\u2013$7k/month)',
                  '40+ hours/week of admin',
                  'Business hours only',
                  'Single-threaded — one task at a time',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* What you're getting */}
            <motion.div
              {...fadeUpView(0.2)}
              className="rounded-2xl border border-accent/30 bg-accent/5 p-8"
            >
              <h3 className="text-xl font-semibold text-foreground mb-6">
                What you're getting
              </h3>
              <ul className="space-y-4">
                {[
                  'AI workforce ($700\u2013$1,500/month)',
                  '24/7 operation — never sleeps',
                  'Instant response time',
                  'Handles multiple requests simultaneously',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 5. FAQ ────────────────────────────────────────────────────── */}
      <section className="landing-section bg-white">
        <div className="landing-container max-w-3xl">
          <motion.p {...fadeUpView()} className="landing-label text-center mb-4">
            FAQ
          </motion.p>
          <motion.h2 {...fadeUpView(0.1)} className="landing-heading text-center mb-16">
            Common questions
          </motion.h2>

          <div className="divide-y divide-border">
            {faqs.map((faq, i) => (
              <motion.div key={i} {...fadeUpView(i * 0.05)}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between py-5 text-left"
                >
                  <span className="text-base font-medium text-foreground pr-4">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={cn(
                      'w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform duration-200',
                      openFaq === i && 'rotate-180'
                    )}
                  />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <p className="pb-5 text-sm text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. Footer CTA + Footer ────────────────────────────────────── */}
      <footer className="bg-primary text-white">
        <div className="landing-container py-24 md:py-32 text-center">
          <motion.h2
            {...fadeUpView()}
            className="text-4xl md:text-5xl font-semibold tracking-tight text-white"
          >
            Ready to hire your AI workforce?
          </motion.h2>

          <motion.p
            {...fadeUpView(0.1)}
            className="mt-6 text-lg text-white/60 max-w-xl mx-auto"
          >
            Start with maintenance. Scale to full operations.
          </motion.p>

          <motion.div
            {...fadeUpView(0.2)}
            className="mt-10"
          >
            <Button
              asChild
              size="xl"
              className="bg-accent text-white hover:bg-accent/90 shadow-lg"
            >
              <Link to={user ? getDashboardLink() : '/auth?mode=signup&role=property_manager'}>
                Get Started
                <ArrowRight className="w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </div>

        <div className="border-t border-white/10">
          <div className="landing-container py-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <BrandLogo size="sm" variant="white" showText={false} />

            <div className="flex items-center gap-6 text-sm text-white/40">
              <Link
                to="/auth?mode=signup&role=tradie"
                className="hover:text-white/70 transition-colors"
              >
                Join as a Tradie
              </Link>
              <Link to="/privacy" className="hover:text-white/70 transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-white/70 transition-colors">
                Terms
              </Link>
            </div>

            <div className="text-sm text-white/30">
              &copy; {new Date().getFullYear()} Agentive Property
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
